import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Field, Section, Toggle } from '@/components/ui/editor-fields';
import { ImageUploadField } from '@/components/ui/image-upload-field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useBusinesses } from '@/features/business/use-businesses';
import { fromInputValue, toInputValue } from '@/lib/store-time';
import { useDeleteBannerAd, useSaveBannerAd } from './banner-ads.api';
import { BANNER_ACTION, PLACEMENT, type BannerAdInput } from './types';

/**
 * The banner editor.
 *
 * Same drawer shape as the info card one, and deliberately so — Marketing edits
 * both, and two near-identical records that behave differently to fill in is a
 * worse outcome than a little repetition.
 *
 * Two departures from the original: the action value is labelled for the action
 * it belongs to rather than reading "Action:" twice in a row under a select
 * that also says "Action", and the approval switch says what it gates.
 */

const ALL_STORES = 'ALL';

/** A hex the native colour input will accept, or its own fallback. */
function swatch(value: string) {
  return /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim() : '#ffffff';
}

export function BannerAdEditor({
  banner,
  open,
  onOpenChange,
}: {
  /** `id` null for a new banner. */
  banner: { id: string | null; values: BannerAdInput } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<BannerAdInput | null>(
    banner?.values ?? null
  );

  const businessesQuery = useBusinesses();
  const businesses = businessesQuery.data;
  const save = useSaveBannerAd();
  const remove = useDeleteBannerAd();

  useEffect(() => {
    setDraft(banner?.values ?? null);
  }, [banner]);

  if (!draft) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl" />
      </Sheet>
    );
  }

  const id = banner?.id ?? null;
  const isEdit = !!id;
  const busy = save.isPending || remove.isPending;

  const set = <K extends keyof BannerAdInput>(
    key: K,
    value: BannerAdInput[K]
  ) => setDraft((current) => (current ? { ...current, [key]: value } : current));

  const startsAfterEnds =
    !!draft.startDate &&
    !!draft.endDate &&
    Date.parse(`${draft.startDate}Z`) >= Date.parse(`${draft.endDate}Z`);

  // An action with nothing to act on is a banner that swallows taps, so the
  // value is required as soon as a type is chosen.
  const needsValue = draft.actionType !== BANNER_ACTION.none;
  const canSave =
    !!draft.name.trim() &&
    !!draft.imageUrl.trim() &&
    !startsAfterEnds &&
    (!needsValue || !!draft.actionValue.trim());

  function submit() {
    save.mutate(
      { id, values: draft! },
      {
        onSuccess: () => {
          toast.success(isEdit ? t('bannerAds.updated') : t('bannerAds.created'));
          onOpenChange(false);
        },
        onError: (error) =>
          toast.error(
            error instanceof Error ? error.message : t('bannerAds.saveFailed')
          ),
      }
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
      >
        <SheetHeader className="flex-row items-center justify-between gap-3 border-b pr-12">
          <SheetTitle>
            {isEdit ? t('bannerAds.editBanner') : t('bannerAds.newBanner')}
          </SheetTitle>
          {isEdit && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  disabled={busy}
                >
                  <Trash2 className="size-3.5" />
                  {t('bannerAds.archive')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('bannerAds.archive')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('bannerAds.archiveConfirm', { name: draft.name })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() =>
                      remove.mutate(id!, {
                        onSuccess: () => {
                          toast.success(t('bannerAds.archived'));
                          onOpenChange(false);
                        },
                        onError: () => toast.error(t('bannerAds.saveFailed')),
                      })
                    }
                  >
                    {t('bannerAds.archive')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </SheetHeader>

        <div className="space-y-6 p-4">
          <Field
            label={t('bannerAds.name')}
            htmlFor="name"
            hint={t('bannerAds.nameHint')}
          >
            <Input
              id="name"
              value={draft.name}
              disabled={busy}
              onChange={(event) => set('name', event.target.value)}
            />
          </Field>

          {/* ----------------------------------------------------- media */}
          <Section title={t('bannerAds.media')}>
            <ImageUploadField
              container="banner"
              label={t('bannerAds.imageEnglish')}
              disabled={busy}
              value={draft.imageUrl}
              onChange={(url) => set('imageUrl', url)}
            />
            <ImageUploadField
              container="banner"
              label={t('bannerAds.imageSpanish')}
              hint={t('bannerAds.imageSpanishHint')}
              disabled={busy}
              value={draft.imageUrlSpa}
              onChange={(url) => set('imageUrlSpa', url)}
            />
            <Field
              label={t('bannerAds.backgroundColor')}
              htmlFor="backgroundColor"
              hint={t('bannerAds.backgroundColorHint')}
            >
              <div className="flex gap-2">
                <Input
                  id="backgroundColor"
                  value={draft.backgroundColor}
                  disabled={busy}
                  placeholder="#ffffff"
                  onChange={(event) =>
                    set('backgroundColor', event.target.value)
                  }
                />
                <input
                  type="color"
                  aria-label={t('bannerAds.backgroundColor')}
                  value={swatch(draft.backgroundColor)}
                  disabled={busy}
                  onChange={(event) =>
                    set('backgroundColor', event.target.value)
                  }
                  className="border-input h-9 w-12 shrink-0 cursor-pointer rounded-md border bg-transparent p-1"
                />
              </div>
            </Field>
          </Section>

          {/* -------------------------------------------------- schedule */}
          <Section title={t('bannerAds.schedule')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('bannerAds.starts')} htmlFor="start">
                <Input
                  id="start"
                  type="datetime-local"
                  value={toInputValue(draft.startDate)}
                  disabled={busy}
                  onChange={(event) =>
                    set('startDate', fromInputValue(event.target.value))
                  }
                />
              </Field>
              <Field label={t('bannerAds.ends')} htmlFor="end">
                <Input
                  id="end"
                  type="datetime-local"
                  value={toInputValue(draft.endDate)}
                  disabled={busy}
                  onChange={(event) =>
                    set('endDate', fromInputValue(event.target.value))
                  }
                />
              </Field>
            </div>
            <p className="text-muted-foreground text-xs">
              {t('bannerAds.datesHint')}
            </p>
            {startsAfterEnds && (
              <p className="text-destructive text-xs">
                {t('bannerAds.endBeforeStart')}
              </p>
            )}
          </Section>

          {/* ------------------------------------------------- placement */}
          <Section title={t('bannerAds.placement')}>
            <Toggle
              label={t('bannerAds.approved')}
              hint={t('bannerAds.approvedHint')}
              checked={draft.approved}
              disabled={busy}
              onChange={(value) => set('approved', value)}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle
                label={t('bannerAds.showInApp')}
                checked={draft.showInApp}
                disabled={busy}
                onChange={(value) => set('showInApp', value)}
              />
              <Toggle
                label={t('bannerAds.showInWeb')}
                checked={draft.showInWeb}
                disabled={busy}
                onChange={(value) => set('showInWeb', value)}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('bannerAds.position')}>
                <Select
                  value={String(draft.placement)}
                  disabled={busy}
                  onValueChange={(value) => set('placement', Number(value))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PLACEMENT).map(([key, value]) => (
                      <SelectItem key={key} value={String(value)}>
                        {t(
                          `bannerAds.placement${key[0].toUpperCase()}${key.slice(1)}`
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={t('bannerAds.ordering')}
                htmlFor="ordering"
                hint={t('bannerAds.orderingHint')}
              >
                <Input
                  id="ordering"
                  type="number"
                  value={draft.ordering}
                  disabled={busy}
                  onChange={(event) =>
                    set('ordering', Number(event.target.value))
                  }
                />
              </Field>
            </div>

            <Field label={t('bannerAds.store')} hint={t('bannerAds.storeHint')}>
              {/* The stored store's name comes from this list, so the trigger
                  would read blank until it arrives. */}
              {businessesQuery.isLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <Select
                  value={draft.businessId ?? ALL_STORES}
                  disabled={busy}
                  onValueChange={(value) =>
                    set('businessId', value === ALL_STORES ? null : value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_STORES}>
                      {t('bannerAds.allStores')}
                    </SelectItem>
                    {(businesses ?? []).map((business) => (
                      <SelectItem key={business.id} value={business.id}>
                        {business.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </Section>

          {/* ---------------------------------------------------- action */}
          <Section title={t('bannerAds.action')}>
            <Field label={t('bannerAds.actionType')}>
              <Select
                value={String(draft.actionType)}
                disabled={busy}
                onValueChange={(value) => set('actionType', Number(value))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(BANNER_ACTION.none)}>
                    {t('bannerAds.actionNone')}
                  </SelectItem>
                  <SelectItem value={String(BANNER_ACTION.openLink)}>
                    {t('bannerAds.actionLink')}
                  </SelectItem>
                  <SelectItem value={String(BANNER_ACTION.search)}>
                    {t('bannerAds.actionSearch')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {/* Labelled for whichever action it belongs to — the original
                called this box "Action" under a select also called "Action". */}
            {needsValue && (
              <Field
                label={
                  draft.actionType === BANNER_ACTION.openLink
                    ? t('bannerAds.linkUrl')
                    : t('bannerAds.searchTerm')
                }
                htmlFor="actionValue"
                hint={
                  draft.actionType === BANNER_ACTION.openLink
                    ? t('bannerAds.linkUrlHint')
                    : t('bannerAds.searchTermHint')
                }
              >
                <Input
                  id="actionValue"
                  value={draft.actionValue}
                  disabled={busy}
                  placeholder={
                    draft.actionType === BANNER_ACTION.openLink ? 'https://…' : ''
                  }
                  onChange={(event) => set('actionValue', event.target.value)}
                />
              </Field>
            )}
          </Section>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={!canSave || busy} onClick={submit}>
              {save.isPending && <Loader2 className="animate-spin" />}
              {isEdit ? t('common.save') : t('bannerAds.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
